import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { messageForError } from '../lib/errors';

/** How often to ask the server whether the link has been opened (another tab, the phone…). */
const POLL_MS = 10_000;

/**
 * Shown to a signed-in user whose email is not confirmed yet (D25). Every
 * other screen is behind `RequireAuth`, which sends unverified users here; the
 * API refuses them with 403 EMAIL_NOT_VERIFIED anyway. Once /auth/me reports
 * `emailVerifiedAt`, the `RequireUnverified` guard moves them along.
 */
export function ConfirmEmailPage() {
  const { auth, refresh, logout } = useAuth();
  const email = auth.status === 'authed' ? auth.user.email : '';
  const [checked, setChecked] = useState<'idle' | 'checking' | 'still-unverified'>('idle');

  const resend = useMutation({ mutationFn: () => api.auth.resendVerification() });

  useEffect(() => {
    const id = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function check() {
    setChecked('checking');
    try {
      const me = await refresh();
      if (!me?.user.emailVerifiedAt) setChecked('still-unverified');
      // When verified, the guard navigates away; nothing to do here.
    } catch {
      setChecked('still-unverified');
    }
  }

  return (
    <AuthLayout title="Confirm your email" subtitle="One more step before you can start planning.">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink">
          We sent a confirmation link to <strong className="font-semibold">{email}</strong>. Open it
          on any device — this page notices and continues on its own.
        </p>
        <p className="text-sm text-muted">
          The link is valid for 24 hours. Nothing arrived? Check your spam folder, then resend.
        </p>

        {resend.isSuccess ? (
          <Alert tone="success">Sent again. Give it a minute, and check your spam folder.</Alert>
        ) : null}
        {resend.isError ? <Alert tone="error">{messageForError(resend.error)}</Alert> : null}
        {checked === 'still-unverified' ? (
          <Alert tone="info">Not confirmed yet. Open the link in the email, then try again.</Alert>
        ) : null}

        <Button type="button" onClick={() => void check()} disabled={checked === 'checking'}>
          {checked === 'checking' ? 'Checking…' : 'I’ve confirmed it'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => resend.mutate()}
          disabled={resend.isPending || resend.isSuccess}
        >
          {resend.isPending ? 'Sending…' : 'Resend the email'}
        </Button>

        <p className="text-center text-xs text-muted">
          Wrong address? Changing it isn’t supported yet: sign out, and create the account again
          with the right one.{' '}
          <button
            type="button"
            onClick={() => void logout()}
            className="font-medium text-brand underline-offset-2 hover:underline"
          >
            Sign out
          </button>
        </p>
      </div>
    </AuthLayout>
  );
}
